import React from "react";
import styled from "styled-components";
import {
  brand_orange_deep,
  brand_gradient,
  card_shadow_md,
  outerContainerCss,
  narrowScreenMediaQuery,
} from "../../constants/styles";
import CardActionsOverlay from "../../homes/CardActionsOverlay";

// Hero band for the course detail page — banner image on the left, course
// name + tagline + stats + primary CTA on the right. Includes the
// like/bookmark overlay so engagement is reachable at the top of the page.
function CourseHero({ course }) {
  if (!course) return null;
  const stats = [
    { value: course.total_curriculums || 0, label: "Modul" },
    { value: course.total_lessons || 0, label: "Materi" },
  ];
  const hasProgress = typeof course.course_progress_percent === "number";
  if (hasProgress) {
    stats.push({ value: `${course.course_progress_percent}%`, label: "Progres", emphasis: true });
  }
  const showJumpCta = !course.coming_soon && !course.locked;

  return (
    <Band>
      <Inner>
        <BannerWrap data-card-host tabIndex={0}>
          {course.banner_image_url ? (
            <BannerImg src={course.banner_image_url} alt={course.name} loading="eager" />
          ) : (
            <BannerPlaceholder aria-hidden="true">{course.name?.[0] || "S"}</BannerPlaceholder>
          )}
          <CardActionsOverlay
            resourceType="Course"
            resourceId={course.id}
            detailLink={null}
            showDetail={false}
            initialLiked={!!course.is_liked}
            initialDisliked={!!course.is_disliked}
            initialBookmarked={!!course.is_bookmarked}
            radius="20px"
          />
        </BannerWrap>
        <Body>
          <Title>{course.name}</Title>
          {/* Suppress descriptions that are too short to be meaningful or
              that look like an admin-pasted date stamp (e.g. "30-05-2022")
              — those slip through from legacy data and read as orphan
              metadata. Real product descriptions are always > 12 chars. */}
          {course.description && course.description.length > 12 && !/^\d{2}-\d{2}-\d{4}$/.test(course.description.trim()) && (
            <Tagline>{course.description}</Tagline>
          )}
          <Stats>
            {stats.map((s) => (
              <StatTile key={s.label} $emphasis={!!s.emphasis}>
                <StatValue $emphasis={!!s.emphasis}>{s.value}</StatValue>
                <StatLabel>{s.label}</StatLabel>
              </StatTile>
            ))}
          </Stats>
          <CtaRow>
            {course.coming_soon ? (
              <ComingSoonPill>Coming Soon</ComingSoonPill>
            ) : course.locked ? (
              <LockedPill>Materi terkunci sementara</LockedPill>
            ) : null}
            {showJumpCta && (
              <JumpCta href="#course-curriculum-list">Lihat Modul →</JumpCta>
            )}
          </CtaRow>
        </Body>
      </Inner>
    </Band>
  );
}

export default React.memo(CourseHero);

const Band = styled.section`
  position: relative;
  background:
    radial-gradient(120% 80% at 12% 0%, rgba(255, 255, 255, 0.9), transparent 55%),
    linear-gradient(135deg, #fff7ed 0%, #ffedd5 100%);
  padding: 64px 0;
  border-bottom: 1px solid rgba(216, 89, 7, 0.18);

  @media ${narrowScreenMediaQuery} {
    padding: 36px 0;
  }

  [data-theme="dark"] & {
    background:
      radial-gradient(120% 80% at 12% 0%, rgba(255, 255, 255, 0.04), transparent 55%),
      linear-gradient(135deg, #1f2937 0%, #0f172a 100%);
    border-bottom-color: rgba(216, 89, 7, 0.32);
  }
`;

const Inner = styled.div`
  ${outerContainerCss}
  display: grid;
  grid-template-columns: 1.1fr 1fr;
  gap: 44px;
  align-items: center;

  @media ${narrowScreenMediaQuery} {
    grid-template-columns: 1fr;
    gap: 22px;
  }
`;

const BannerWrap = styled.div`
  position: relative;
  aspect-ratio: 16 / 10;
  border-radius: 20px;
  overflow: hidden;
  background: linear-gradient(135deg, #e5e7eb, #cbd5e1);
  box-shadow: ${card_shadow_md}, 0 0 0 1px rgba(216, 89, 7, 0.18);

  [data-theme="dark"] & {
    box-shadow: ${card_shadow_md}, 0 0 0 1px rgba(216, 89, 7, 0.32);
  }
`;

const BannerImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const BannerPlaceholder = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 96px;
  font-weight: 800;
  color: rgba(255, 255, 255, 0.7);
`;

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const Title = styled.h1`
  font-size: 44px;
  font-weight: 800;
  margin: 0;
  color: ${brand_orange_deep};
  line-height: 1.15;
  letter-spacing: -0.01em;

  @media ${narrowScreenMediaQuery} { font-size: 28px; }
`;

const Tagline = styled.p`
  margin: 0;
  font-size: 17px;
  line-height: 1.65;
  color: #4b5563;

  [data-theme="dark"] & { color: #d1d5db; }
`;

const Stats = styled.div`
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
`;

const StatTile = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 12px 18px;
  border-radius: 12px;
  border: 1px solid ${({ $emphasis }) => ($emphasis ? "rgba(216, 89, 7, 0.32)" : "rgba(15, 23, 42, 0.08)")};
  background: ${({ $emphasis }) =>
    $emphasis
      ? "linear-gradient(135deg, rgba(216, 89, 7, 0.10), rgba(247, 179, 12, 0.08))"
      : "rgba(255, 255, 255, 0.6)"};
  min-width: 96px;

  [data-theme="dark"] & {
    background: ${({ $emphasis }) =>
      $emphasis ? "rgba(216, 89, 7, 0.18)" : "rgba(255, 255, 255, 0.04)"};
    border-color: ${({ $emphasis }) =>
      $emphasis ? "rgba(216, 89, 7, 0.45)" : "rgba(255, 255, 255, 0.10)"};
  }
`;

const StatValue = styled.span`
  font-size: 22px;
  font-weight: 800;
  color: ${({ $emphasis }) => ($emphasis ? brand_orange_deep : "#1f2937")};
  font-variant-numeric: tabular-nums;

  [data-theme="dark"] & { color: ${({ $emphasis }) => ($emphasis ? "#fdba74" : "#f3f4f6")}; }
`;

const StatLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
  color: #6b7280;

  [data-theme="dark"] & { color: #9ca3af; }
`;

const CtaRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
`;

const JumpCta = styled.a`
  display: inline-block;
  padding: 12px 24px;
  border-radius: 999px;
  background: ${brand_orange_deep};
  color: #fff;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  text-decoration: none;
  transition: filter 150ms ease, transform 150ms ease;

  &:hover { filter: brightness(0.95); transform: translateY(-1px); color: #fff; }
  &:focus-visible { outline: 2px solid #fdba74; outline-offset: 2px; }
`;

const ComingSoonPill = styled.span`
  display: inline-block;
  padding: 8px 18px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 13px;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  background: ${brand_gradient};
  color: #fff;
  width: fit-content;
`;

const LockedPill = styled.span`
  display: inline-block;
  padding: 8px 18px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 13px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  width: fit-content;
`;
